import {useState} from 'react'
import {useFieldArray, useForm} from 'react-hook-form'
import {
  SalesPromoSelectGiftModel,
  SalesPromoSelectGiftPrizesModel,
} from 'src/app/modules/campaign/models/SalesPromo.model'
import {
  useSalesPromoSelectPrizeMission,
  useSubmitSalesPromoPrizeMission,
} from 'src/app/modules/campaign/providers/SalesPromo.provider'
import {MallModel} from 'src/app/modules/directory/models'
import { getErrorMessage } from 'src/app/utils/api-utils'
import {safeArray} from 'src/app/utils/array-utils'
import { Refs } from 'src/app/utils/refs-utils'

export interface SelectGiftFormModel {
  directory?: MallModel
  claim_directory_id?: string
  prizes: SalesPromoSelectGiftPrizesModel[]
  direct_prizes?: SalesPromoSelectGiftPrizesModel[]
  direct_prizes_quantity?: number
  extra_point_prize?: SalesPromoSelectGiftPrizesModel
}

export function useSelectGiftPopup(
  {
    campaign_id,
    member_id,
    mission_stamp_id,
  }: {
    campaign_id?: string
    member_id?: string
    mission_stamp_id?: string
  },
  clickCancel: () => void
) {
  const detail = useSalesPromoSelectPrizeMission(
    campaign_id,
    member_id,
    mission_stamp_id,
    (data: SalesPromoSelectGiftModel) => {
      selectGiftForm.reset()
      data.prizes?.forEach((element: SalesPromoSelectGiftPrizesModel) => {
        const {type, prizes} = element
        const newEl = {...element, quantity: 0}
        if (type === 'PRODUCT' && prizes) {
          const remakePrizesTypes = prizes?.map((elPrize) => {
            return {...elPrize, type: 'PRODUCT', quantity: 1}
          })
          setDirectGiftOptions(remakePrizesTypes as SalesPromoSelectGiftPrizesModel[])
        } else if (type === 'EXTRA_POINT') {
          selectGiftForm.setValue('extra_point_prize', newEl)
        } else {
          appendGift(newEl)
        }
      })
    },
    (error) => {
      Refs.pageToast.show({
        severity: 'danger',
        message: `${getErrorMessage(error, false)?.en}`,
      })
      clickCancel()
    }
  )

  const submit = useSubmitSalesPromoPrizeMission(campaign_id, member_id, mission_stamp_id)

  const selectGiftDefaultForm: SelectGiftFormModel = {
    claim_directory_id: undefined,
    directory: undefined,
    prizes: [],
    direct_prizes: [],
    direct_prizes_quantity: 0,
    extra_point_prize: undefined,
  }
  const selectGiftForm = useForm<SelectGiftFormModel>({
    defaultValues: selectGiftDefaultForm,
  })
  // useQueryFields({
  //   mutation: saveRole,
  //   query: role,
  //   form: selectGiftForm,
  // })

  const [directGiftOptions, setDirectGiftOptions] = useState<SalesPromoSelectGiftPrizesModel[]>()

  const {
    control: controlGift,
    setValue: setValueGift,
    getValues: getValuesGift,
    clearErrors: clearErrorsGift,
    reset: resetGift,
  } = selectGiftForm
  const {
    fields: fieldsGift,
    append: appendGift,
    remove: removeGift,
  } = useFieldArray({
    control: controlGift,
    name: `prizes`,
  })

  const exPointFieldsIdx = fieldsGift.findIndex((arg) => arg.type === 'EXTRA_POINT')

  const {
    control: controlDirectGift,
    setValue: setValueDirectGift,
    getValues: getValuesDirectGift,
    clearErrors: clearErrorsDirectGift,
    reset: resetDirectGift,
  } = selectGiftForm
  const {
    fields: fieldsDirectGift,
    append: appendDirectGift,
    remove: removeDirectGift,
  } = useFieldArray({
    control: controlDirectGift,
    name: `direct_prizes`,
  })

  const hasDirectGift = detail?.data?.prizes?.some(
    (it: SalesPromoSelectGiftPrizesModel) => it.type === 'PRODUCT'
  )
  const valClaimLocation = selectGiftForm.getValues('directory')?.id
  const valDirectPrizeQuantity = selectGiftForm.getValues('direct_prizes_quantity')
  const valPrizes = selectGiftForm.getValues('prizes')
  const valDirectGift = selectGiftForm.getValues('direct_prizes')
  const valExtraPoint = selectGiftForm.getValues('extra_point_prize')
  const lasItemRandomGift = valDirectGift?.at(-1)
  const disableIncreaseRandomGift =
    !lasItemRandomGift?.id && valDirectGift && valDirectGift.length > 0
  const totalDirectGift = valDirectGift?.filter((it) => it?.quantity)?.length || 0
  const totalPhysical = valPrizes
    ?.filter((el) => el.type === 'FISIK')
    .reduce((sum, it) => sum + (it.quantity ?? 0), 0)
  const totalDigital = valPrizes
    ?.filter((el) => el.type === 'DIGITAL')
    .reduce((sum, it) => sum + (it.quantity ?? 0), 0)
  const totalFreeParking = valPrizes
    ?.filter((el) => el.type === 'PARKING')
    .reduce((sum, it) => sum + (it.quantity ?? 0), 0)
  const totalValetParking = valPrizes
    ?.filter((el) => el.type === 'VALET')
    .reduce((sum, it) => sum + (it.quantity ?? 0), 0)
  const totalStamp = valPrizes
    ?.filter((el) => el.type === 'STAMP')
    .reduce((sum, it) => sum + (it.quantity ?? 0), 0)

  const summaryTable = [
    ...(hasDirectGift ? [{name: 'Direct Gift', quantity: totalDirectGift}] : []),
    ...(detail?.data?.prizes?.some((it: SalesPromoSelectGiftPrizesModel) => it.type === 'FISIK')
      ? [{name: 'Physical Voucher', quantity: totalPhysical}]
      : []),
    ...(detail?.data?.prizes?.some((it: SalesPromoSelectGiftPrizesModel) => it.type === 'DIGITAL')
      ? [{name: 'E-Voucher', quantity: totalDigital}]
      : []),
    ...(detail?.data?.prizes?.some(
      (it: SalesPromoSelectGiftPrizesModel) => it.type === 'PARKING' || it.type === 'VALET'
    )
      ? [{name: 'Free Parking/Valet', quantity: totalFreeParking + totalValetParking}]
      : []),
    ...(detail?.data?.prizes?.some((it: SalesPromoSelectGiftPrizesModel) => it.type === 'STAMP')
      ? [{name: 'Stamp', quantity: totalStamp}]
      : []),
    ...(detail?.data?.prizes?.some(
      (it: SalesPromoSelectGiftPrizesModel) => it.type === 'EXTRA_POINT'
    )
      ? [{name: 'Point', quantity: valExtraPoint?.quantity || 0}]
      : []),
  ]

  const totalPrizeCollected =
    summaryTable?.reduce(
      (sum, it) => sum + (it.name === 'Point' ? (it.quantity ? 1 : 0) : it.quantity ?? 0),
      0
    ) || 0
  const disableAllIncrement = totalPrizeCollected >= (detail?.data?.prize_quantity_per_round || 0)

  const directGiftChange = ({
    name,
    val,
    type,
  }: {
    name: string
    val: number
    type: 'inc' | 'dec'
  }) => {
    if (type === 'inc') {
      appendDirectGift(undefined as any)
    } else {
      const target = valDirectGift?.[val]
      // Check if any option related to the selected item is already disabled
      const shouldEnable = directGiftOptions?.some((el) => el?.id === target?.id && el?.isDisabled)
      if (shouldEnable) {
        setDirectGiftOptions((prevVal?: SalesPromoSelectGiftPrizesModel[]) => {
          // Create new options, marking the matched ones with isDisabled: false
          const updatedOptions = safeArray(prevVal).map((item) => {
            if (item?.id === target?.id) {
              return {...item, isDisabled: false}
            }
            return item
          })
          return updatedOptions
        })
      }
      removeDirectGift(val)
    }
  }

  const changeValueDirectPrize = (selected: SalesPromoSelectGiftPrizesModel, index: number) => {
    selectGiftForm.setValue(`direct_prizes.${index}`, selected)
    updateDisablingDirectGiftItem()
  }

  const updateDisablingDirectGiftItem = () => {
    setDirectGiftOptions((prevVal?: SalesPromoSelectGiftPrizesModel[]) => {
      const giftCounts: Record<string, number> = {}
      // Count how many times each id appears in valDirectGift
      safeArray(valDirectGift).forEach((item) => {
        if (item?.id) {
          giftCounts[item.id] = (giftCounts[item.id] || 0) + 1
        }
      })
      // Now update disabled state based on counts
      const updatedOptions = safeArray(prevVal).map((item) => {
        const count = giftCounts[item.id!] || 0
        const remaining = item.remaining_quantity || 1
        if (count >= remaining) {
          return {...item, isDisabled: true}
        }
        return {...item, isDisabled: false} // optional: reset disabled
      })
      return updatedOptions
    })
  }

  const reset = () => {
    selectGiftForm.reset()
    setDirectGiftOptions(undefined)
  }
  selectGiftForm.watch() // this is necessary to solve a common gotcha problem with react-hook-form and useFieldArray
  return {
    selectGiftForm,
    reset,
    valClaimLocation,
    detail,
    hasDirectGift,
    totalPrizeCollected,
    disableAllIncrement,
    exPointFieldsIdx,
    gift: {
      fields: fieldsGift,
      append: appendGift,
      remove: removeGift,
    },
    directGift: {
      directGiftChange,
      changeValueDirectPrize,
      directGiftOptions,
      setDirectGiftOptions,
      fields: fieldsDirectGift,
      append: appendDirectGift,
      remove: removeDirectGift,
    },
    valDirectPrizeQuantity,
    valPrizes,
    valDirectGift,
    valExtraPoint,
    lasItemRandomGift,
    disableIncreaseRandomGift,
    summaryTable,
    submit,
  }
}
